const ensureArray = (value) => (Array.isArray(value) ? value : []);

const toPoint = (timestamp, value) => {
    if (!timestamp || value === undefined || value === null) return null;
    const x = new Date(timestamp);
    if (Number.isNaN(x.getTime())) return null;
    return { x, y: Number(value) };
};

const toNumber = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const addPoint = (series, key, timestamp, value) => {
    const point = toPoint(timestamp, value);
    if (!point) return;
    if (!series[key]) {
        series[key] = [];
    }
    series[key].push(point);
};

const isPrivateIpv4 = (ip) => {
    if (!ip || typeof ip !== 'string' || ip.includes(':')) return false;
    const parts = ip.split('.').map((part) => Number(part));
    if (parts.length !== 4 || parts.some((part) => Number.isNaN(part))) return false;
    const [a, b] = parts;
    return (
        a === 10 ||
        (a === 172 && b >= 16 && b <= 31) ||
        (a === 192 && b === 168)
    );
};

const lossPercentage = (lost, sent) => {
    const lostNum = toNumber(lost);
    const sentNum = toNumber(sent);
    if (lostNum === null || sentNum === null || sentNum <= 0) return null;
    return (lostNum / sentNum) * 100;
};

const transformMtrMeasurements = (measurements = []) => {
    const gateway = {};
    const isp = {};
    const endpoint = {};
    const hopCounts = {};
    const packetLossGateway = {};
    const packetLossIsp = {};
    const packetLossEndpoint = {};

    ensureArray(measurements).forEach((measurement) => {
        const target = measurement.target || measurement.endpoint || 'unknown';
        const timestamp = measurement.ts || measurement.timestamp;
        const hops = ensureArray(measurement.hops);
        if (!timestamp || hops.length === 0) return;

        const privateHops = hops.filter((hop) => isPrivateIpv4(hop.hop_ip || hop.ip));
        const gatewayHop = privateHops.length ? privateHops[privateHops.length - 1] : null;
        const publicHops = hops.filter((hop) => !isPrivateIpv4(hop.hop_ip || hop.ip));
        const ispHop = publicHops[0];
        const endpointHop = publicHops.length ? publicHops[publicHops.length - 1] : null;

        if (gatewayHop) {
            addPoint(gateway, target, timestamp, (val => val != null ? val / 1000 : null)(toNumber(gatewayHop.t_avg ?? gatewayHop.t_avg_ms ?? gatewayHop.duration_ms)));
            const loss = lossPercentage(gatewayHop.lost, gatewayHop.sent);
            if (loss !== null) addPoint(packetLossGateway, target, timestamp, loss);
        }

        if (ispHop) {
            addPoint(isp, target, timestamp, (val => val != null ? val / 1000 : null)(toNumber(ispHop.t_avg ?? ispHop.t_avg_ms ?? ispHop.duration_ms)));
            const loss = lossPercentage(ispHop.lost, ispHop.sent);
            if (loss !== null) addPoint(packetLossIsp, target, timestamp, loss);
        }

        if (endpointHop) {
            addPoint(endpoint, target, timestamp, (val => val != null ? val / 1000 : null)(toNumber(endpointHop.t_avg ?? endpointHop.t_avg_ms ?? endpointHop.duration_ms)));
            const loss = lossPercentage(endpointHop.lost, endpointHop.sent);
            if (loss !== null) addPoint(packetLossEndpoint, target, timestamp, loss);
        }

        addPoint(hopCounts, target, timestamp, hops.length);
    });

    return {
        gateway: { label: 'Gateway Hop Duration', series: gateway },
        isp: { label: 'ISP Hop Duration', series: isp },
        endpoint: { label: 'Endpoint Hop Duration', series: endpoint },
        hopCounts: { label: 'Hop Count', series: hopCounts },
        packetLossGateway: { label: 'Packet Loss (Gateway)', series: packetLossGateway },
        packetLossIsp: { label: 'Packet Loss (ISP)', series: packetLossIsp },
        packetLossEndpoint: { label: 'Packet Loss (Endpoint)', series: packetLossEndpoint },
    };
};

export const transformDnsSeries = (dnsRecords = []) => {
    const pingSeries = {};
    const resolveSeries = {};

    ensureArray(dnsRecords).forEach((record) => {
        const hostname = record.hostname || record.host || 'unknown';
        const timestamp = record.timestamp;
        const rawPing = record.ping_duration_ms ?? record.ping_ms; const pingPoint = toPoint(timestamp, rawPing != null ? rawPing / 1000 : null);
        const rawResolve = record.resolution_time_ms ?? record.resolve_ms; const resolvePoint = toPoint(timestamp, rawResolve != null ? rawResolve / 1000 : null);

        if (pingPoint) {
            if (!pingSeries[hostname]) pingSeries[hostname] = [];
            pingSeries[hostname].push(pingPoint);
        }

        if (resolvePoint) {
            if (!resolveSeries[hostname]) resolveSeries[hostname] = [];
            resolveSeries[hostname].push(resolvePoint);
        }
    });

    return { pingSeries, resolveSeries };
};

export const transformHttpSeries = (httpRecords = []) => {
    const series = {};
    ensureArray(httpRecords).forEach((record) => {
        const url = record.url || record.target || 'unknown';
        const rawHttp = record.response_time_ms ?? record.duration_ms; const point = toPoint(record.ts || record.timestamp, rawHttp != null ? rawHttp / 1000 : null);
        if (point) {
            if (!series[url]) series[url] = [];
            series[url].push(point);
        }
    });
    return series;
};

const extractMtrSeries = (records = [], valueKey, label) => {
    const series = {};
    ensureArray(records).forEach((record) => {
        const endpoint = record.target || record.endpoint || 'unknown';
        const rawVal = record[valueKey]; const val = valueKey.includes('duration') || valueKey.includes('ms') ? rawVal / 1000 : rawVal; const point = toPoint(record.ts || record.timestamp, val);
        if (point) {
            if (!series[endpoint]) series[endpoint] = [];
            series[endpoint].push(point);
        }
    });
    return { label, series };
};

export const transformMtrSeries = (mtrData = {}) => {
    if (Array.isArray(mtrData)) {
        return transformMtrMeasurements(mtrData);
    }

    const {
        gwHopDurations = [],
        ispHopDurations = [],
        endpointHopDurations = [],
        hopCounts = [],
        gwPacketLoss = [],
        ispPacketLoss = [],
        endpointPacketLoss = [],
    } = mtrData;

    return {
        gateway: extractMtrSeries(gwHopDurations, 'duration_ms', 'Gateway Hop Duration'),
        isp: extractMtrSeries(ispHopDurations, 'duration_ms', 'ISP Hop Duration'),
        endpoint: extractMtrSeries(endpointHopDurations, 'duration_ms', 'Endpoint Hop Duration'),
        hopCounts: extractMtrSeries(hopCounts, 'count', 'Hop Count'),
        packetLossGateway: extractMtrSeries(gwPacketLoss, 'loss_percent', 'Packet Loss (Gateway)'),
        packetLossIsp: extractMtrSeries(ispPacketLoss, 'loss_percent', 'Packet Loss (ISP)'),
        packetLossEndpoint: extractMtrSeries(endpointPacketLoss, 'loss_percent', 'Packet Loss (Endpoint)'),
    };
};

export const transformWanPayload = (payload = {}) => {
    const dns = transformDnsSeries(payload.dns);
    const http = transformHttpSeries(payload.http);
    const mtr = transformMtrSeries(payload.mtr);

    return {
        dns,
        http,
        mtr,
        raw: payload,
    };
};
